import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SanityImage from 'gatsby-plugin-sanity-image';
import Button from '../../button';
import breakpoints from '../../breakpoints';
import sharedStyles from '../../styles';
import BlockBuilder from '../BlockBuilder';

const Container = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  margin-block: ${sharedStyles.spacing['spacing-1xl']};
`;

const TextContainer = styled.div`
  position: relative;
  color: ${({ theme }) => theme.colors.cardText};
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border: 2px solid ${({ theme }) => theme.colors.border};
  padding: ${sharedStyles.spacing['spacing-lg']}
    ${sharedStyles.spacing['spacing-1xl']};
  margin: -${sharedStyles.spacing['spacing-2xl']} ${sharedStyles.spacing[
      'spacing-lg'
    ]} ${sharedStyles.spacing['spacing-lg']};
  box-shadow: 0px 3px 36px -10px rgba(0, 0, 0, 0.75);

  @media screen and (min-width: ${breakpoints.xl}) {
    position: absolute;
    margin: 0 0 0 calc(50% - 614px);
    transform: translateY(-50%);
    top: 50%;
    right: 50%;
  }
`;

const ImageBanner = styled.div`
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;

  @media screen and (min-width: ${breakpoints.md}) {
    aspect-ratio: 21 / 9;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;

    ${({ hotspot }) =>
      hotspot &&
      `
      aspect-ratio: 3/2;

      @media (min-width: ${breakpoints.lg}) {
        aspect-ratio: 3/1;
      }

      @media (min-width: ${breakpoints.xl}) {
        aspect-ratio: 42/9;
      }
    `}
  }
`;

const getBreakpoint = breakpoint => breakpoint.replace('px', '');

const getImageWidth = value => {
  let imageWidth = value?.media?.asset?.metadata?.dimensions?.width;

  if (typeof window !== 'undefined' && imageWidth) {
    const screenWidth = window?.innerWidth;

    if (screenWidth < imageWidth) {
      if (screenWidth < getBreakpoint(breakpoints.xs)) {
        imageWidth = getBreakpoint(breakpoints.xs);
      } else if (screenWidth < getBreakpoint(breakpoints.sm)) {
        imageWidth = getBreakpoint(breakpoints.sm);
      } else if (screenWidth < getBreakpoint(breakpoints.md)) {
        imageWidth = getBreakpoint(breakpoints.md);
      } else if (screenWidth < getBreakpoint(breakpoints.lg)) {
        imageWidth = getBreakpoint(breakpoints.lg);
      } else if (screenWidth < getBreakpoint(breakpoints.xl)) {
        imageWidth = getBreakpoint(breakpoints.xl);
      } else {
        imageWidth = 1350;
      }
    }
  }

  return imageWidth;
};

function BlockBuilderImageBannerCta({ value, getInternalLink }) {
  const [imageSize, setImageSize] = useState(null);

  useEffect(() => {
    const windowResizeHandle = () => setImageSize(getImageWidth(value));
    window.addEventListener('resize', windowResizeHandle);

    setImageSize(getImageWidth(value));
  }, []);

  return (
    <Container className="blockbuilder-image-banner-cta">
      <ImageBanner>
        {value?.media?.asset && (
          <SanityImage
            asset={value?.media?.asset}
            hotspot={value?.media?.hotspot}
            crop={value?.media?.crop}
            alt={value?.media?.alt || ''}
            width={parseInt(imageSize, 10)}
            options={{ __experimentalAspectRatio: true }}
            loading="eager"
            sizes={`(max-width: ${breakpoints.xs}) 480px,
                    (max-width: ${breakpoints.sm}) 640px,
                    (max-width: ${breakpoints.md}) 768px,
                    (max-width: ${breakpoints.lg}) 1024px,
                    (max-width: ${breakpoints.xl}) 1280px,
                    1350px`}
          />
        )}
      </ImageBanner>
      <TextContainer>
        {value?.heading && <h2>{value.heading}</h2>}
        {value?.blockBuilderText?.text?.length && (
          <BlockBuilder
            blocks={value.blockBuilderText.text}
            getInternalLink={getInternalLink}
          />
        )}
        {value?.cta?.linkType === 'internal' && (
          <Button
            to={getInternalLink({
              ...value.cta,
              reference: value.cta?.internalLink,
            })}
          >
            {value?.cta?.caption}
          </Button>
        )}
        {value?.cta?.linkType === 'external' && (
          <Button
            href={value?.cta?.href}
            target={value?.cta?.blank ? '_blank' : '_self'}
            rel="noreferrer"
          >
            {value?.cta?.caption}
          </Button>
        )}
      </TextContainer>
    </Container>
  );
}

BlockBuilderImageBannerCta.propTypes = {
  value: PropTypes.shape({
    media: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
        metadata: PropTypes.shape({
          dimensions: PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        }),
      }),
      hotspot: PropTypes.shape({}),
      crop: PropTypes.shape({}),
      alt: PropTypes.string,
    }),
    heading: PropTypes.string,
    blockBuilderText: PropTypes.shape({
      text: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    cta: PropTypes.shape({
      caption: PropTypes.string,
      linkType: PropTypes.string,
      internalLink: PropTypes.shape({}),
      href: PropTypes.string,
      blank: PropTypes.bool,
    }),
  }).isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default BlockBuilderImageBannerCta;
